// src/App.js

import React from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import useSessionStorage from "./useSessionStorage";
import useAxios from "./useAxios";

import NavBar from "./NavBar";
import MatchTables from "./MatchTables";
import Login from "./Login";
import Scoreboard from "./Scoreboard";
import Competition from "./Competitions/Competition";
import CompetitionDraw from "./Competitions/CompetitionDraw";
import CompetitionsList from "./Competitions/CompetitionsList";
import ScoreboardOverlay from "./ScoreboardOverlay";
import TenantsList from "./TenantList";
import Tenant from "./Tenant";

import DartsScoreboard from "./Scoreboards/Darts/DartsScoreboard";
import CrashScoreboard from "./Scoreboards/Cards/Crash/CrashScoreboard";

import PlayersList from "./PlayersList";
import Player from "./Player";
import TablesList from "./TablesList";
import Table from "./Table";
import MatchesList from "./MatchesList";
import Match from "./Match";
import ProductsList from "./ProductsList";
import Product from "./Product";
import TabDetails from "./TabDetails";
import TabletLogin from "./TabletLogin";
import ConfigurationDashboard from "./ConfigurationDashboard";
import OrdersList from "./OrdersList";
import Timer from "./Timer";

import PokerDashboard from "./Poker/PokerDashboard";
import PokerTournementsList from "./Poker/PokerTournementsList";
import PokerTournament from "./Poker/PokerTournament";
import PokerTimer from "./Poker/PokerTimer";

import User from "./User";
import UsersList from "./UsersList";
import StreamType from "./StreamType";
import StreamTypesList from "./StreamTypesList";
import RequiresAdmin from "./RequiresAdmin";
import RequiresAuthAdmin from "./RequiresAuthAdmin";
import UserProfile from "./UserProfile";
import UserTournamentsList from "./UserTournamentsList";
import UserTournament from "./UserTournament";
import UserCompetitions from "./User/UserCompetitions";
import UserCompetitionView from "./User/UserCompetitionView";
import PublicCompetitionDraw from "./PublicCompetitionDraw";

const App = () => {
  const [token, setToken] = useSessionStorage("token", null);
  const [axios] = useAxios(true);

  // Initialize tenant with cached data to avoid logo flash
  const getCachedTenantInfo = () => {
    try {
      const cached = localStorage.getItem("cachedTenantInfo");
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error parsing cached tenant info:", error);
      return null;
    }
  };

  const [tenant, setTenant] = useSessionStorage(
    "tenant",
    getCachedTenantInfo()
  );
  const [version, setVersion] = useSessionStorage("version", null);
  const navigate = useNavigate();
  const [logoutPath, setLogoutPath] = React.useState("/");
  const [isFullscreen, setIsFullscreen] = useSessionStorage(
    "isFullscreen",
    false
  );
  const [menus, setMenus] = useSessionStorage("menus", []);

  // Function to get user type from token
  const getUserType = (token) => {
    if (!token) return "user";
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_type || "user";
    } catch (error) {
      console.error("Error decoding token:", error);
      return "user";
    }
  };

  // Function to get match ID from token for match users
  const getMatchIdFromToken = (token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      // First try to get from match_id claim
      if (payload.match_id) {
        return payload.match_id;
      }

      // Fallback: extract from unique_name (format: "match-user-{matchId}")
      if (
        payload.unique_name &&
        payload.unique_name.startsWith("match-user-")
      ) {
        const matchId = payload.unique_name.replace("match-user-", "");
        return matchId;
      }

      return null;
    } catch (error) {
      console.error("Error decoding match ID from token:", error);
      return null;
    }
  };

  // Function to lookup table ID for a match and redirect to scoreboard
  const lookupTableForMatch = (matchId, authToken) => {
    // Create headers with the specific token for this request
    const headers = {
      "Content-Type": "application/json",
      Authorization: "bearer " + authToken,
      Origin: window.location.origin,
    };

    const buildUrl = (api) => {
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const cleanBase = baseUrl.replace(/\/+$/, "");
      const cleanApi = api.replace(/^\/+/, "");
      return cleanBase + "/" + cleanApi;
    };

    fetch(buildUrl(`match/${matchId}`), {
      method: "GET",
      headers: headers,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .then((matchData) => {
        if (matchData && matchData.table && matchData.table.id) {
          navigate(`/scoreboard/${matchData.table.id}/match/${matchId}`);
        } else {
          console.error("Match has no table assigned");
          navigate("/login");
        }
      })
      .catch((error) => {
        console.error("Error looking up match table:", error);
        navigate("/login");
      });
  };

  const handleLogin = (data) => {
    const userType = getUserType(data.token);

    // Check for stored redirect URL
    const redirectUrl = sessionStorage.getItem("redirectAfterLogin");

    if (userType === "match-user") {
      // Clear any stored redirect URL for match users as they have a specific destination
      sessionStorage.removeItem("redirectAfterLogin");

      // Get match ID from token for match users
      const matchId = getMatchIdFromToken(data.token);
      setMenus([]); // No menus for match users
      setTenant(data.tenant);
      setVersion(data.version);
      setToken(data.token);
      if (matchId) {
        // Look up the table ID for this match, then redirect to new route format
        lookupTableForMatch(matchId, data.token);
      }
      return;
    } else if (userType === "admin") {
      setMenus([
        { label: "Home", url: "/" },
        { label: "Competitions", url: "/admin/competitions" },
        { label: "Matches", url: "/matchtables" },
        { label: "Configuration", url: "/configuration/" },
        //{ label: "Poker", url: "/poker/dashboard" },
      ]);
    } else {
      setMenus([
        { label: "Profile", url: "/profile" },
        { label: "Competitions", url: "/user/competitions" },
      ]);
    }

    setTenant(data.tenant);
    setToken(data.token);
    setVersion(data.version);

    // Handle stored redirect URL
    if (redirectUrl) {
      // Validate redirect URL based on user type
      const isAdminRoute =
        redirectUrl.startsWith("/admin/") ||
        redirectUrl.startsWith("/configuration/") ||
        redirectUrl === "/matchtables" ||
        redirectUrl === "/";
      const isUserRoute =
        redirectUrl.startsWith("/user/") ||
        redirectUrl === "/profile" ||
        redirectUrl.startsWith("/tournaments");

      // Only allow redirect if route matches user type
      if (
        (userType === "admin" && (isAdminRoute || isUserRoute)) ||
        (userType === "user" && isUserRoute)
      ) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectUrl);
        return; // Early return to prevent default navigation
      } else {
        // Clear invalid redirect URL
        sessionStorage.removeItem("redirectAfterLogin");
      }
    }

    // Default navigation based on user type
    if (userType === "admin") {
      navigate("/");
    } else {
      navigate("/profile");
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Not in fullscreen, so request it
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE11 */
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Already in fullscreen, so exit it
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE11 */
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const handleTabletLogin = (data) => {
    setLogoutPath("/login/tablet/" + data.id);
    setToken(data.token);
    navigate("/tablet/tab/" + data.id);
  };

  const logout = () => {
    setToken(null);
    navigate(logoutPath);
  };

  const RequiresAuth = ({ children }) => {
    if (!token) {
      // Store the current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      return <Navigate to="/login" />;
    }

    return (
      <div>
        {React.Children.map(children, (child) => {
          // Clone the child with additional props
          return React.cloneElement(child, { logout });
        })}
      </div>
    );
  };

  const RequiresAuthOrMatchUser = ({ children }) => {
    if (!token) {
      // Store the current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      return <Navigate to="/login" />;
    }

    const userType = getUserType(token);

    // For match users, we need to validate they can only access scoreboards for their match
    // But since they're redirected to /scoreboard/{tableId}, we'll let the backend validate
    // that the specific match is on this table via the API call

    return (
      <div>
        {React.Children.map(children, (child) => {
          // Clone the child with additional props
          return React.cloneElement(child, { logout });
        })}
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <NavBar
        visible={token !== null && getUserType(token) !== "match-user"}
        menus={menus}
        version={version}
        logout={logout}
        toggleFullscreen={toggleFullscreen}
      />
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route
          path="/login/match/:matchId"
          element={<Login onLogin={handleLogin} />}
        />
        <Route path="/login/match" element={<Login onLogin={handleLogin} />} />
        <Route
          path="/login/tablet/:tabId"
          element={<TabletLogin onLogin={handleTabletLogin} />}
        />
        <Route
          path="/profile"
          element={
            <RequiresAuth>
              <UserProfile />
            </RequiresAuth>
          }
        />
        <Route
          path="/tournaments"
          element={
            <RequiresAuth>
              <UserTournamentsList />
            </RequiresAuth>
          }
        />
        <Route
          path="/tournament/:id"
          element={
            <RequiresAuth>
              <UserTournament />
            </RequiresAuth>
          }
        />
        <Route
          path="/"
          element={
            <RequiresAuthAdmin>
              <MatchTables />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/darts/scoreboard"
          element={
            <RequiresAuthAdmin>
              <DartsScoreboard />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/matchtables"
          element={
            <RequiresAuthAdmin>
              <MatchTables />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/scoreboard/:id"
          element={
            <RequiresAuthOrMatchUser>
              <Scoreboard />
            </RequiresAuthOrMatchUser>
          }
        />
        <Route
          path="/scoreboard/:id/match/:matchid"
          element={
            <RequiresAuthOrMatchUser>
              <Scoreboard />
            </RequiresAuthOrMatchUser>
          }
        />
        <Route
          path="/configuration/matches"
          element={
            <RequiresAuthAdmin>
              <MatchesList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/match/:id"
          element={
            <RequiresAuthAdmin>
              <Match />
            </RequiresAuthAdmin>
          }
        />
        {/* Admin Competition Routes */}
        <Route
          path="/admin/competitions"
          element={
            <RequiresAuthAdmin>
              <CompetitionsList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/admin/competitions/:id"
          element={
            <RequiresAuthAdmin>
              <Competition />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/admin/competitions/:id/draw"
          element={
            <RequiresAuthAdmin>
              <CompetitionDraw />
            </RequiresAuthAdmin>
          }
        />

        {/* User Competition Routes */}
        <Route
          path="/user/competitions"
          element={
            <RequiresAuth>
              <UserCompetitions />
            </RequiresAuth>
          }
        />
        <Route
          path="/user/competitions/:id"
          element={
            <RequiresAuth>
              <UserCompetitionView />
            </RequiresAuth>
          }
        />
        <Route
          path="/user/competitions/:id/draw"
          element={
            <RequiresAuth>
              <CompetitionDraw readonly={true} />
            </RequiresAuth>
          }
        />

        {/* Legacy routes for backward compatibility */}
        <Route
          path="/competitions"
          element={
            <RequiresAuthAdmin>
              <CompetitionsList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/competition/:id"
          element={
            <RequiresAuthAdmin>
              <Competition />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/competition/:id/draw"
          element={
            <RequiresAuthAdmin>
              <CompetitionDraw />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/public/scoreboard-overlay/:id"
          element={<ScoreboardOverlay />}
        />
        <Route
          path="/public/competition/:id/draw"
          element={<PublicCompetitionDraw />}
        />
        <Route
          path="/configuration/players"
          element={
            <RequiresAuthAdmin>
              <PlayersList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/player/:id"
          element={
            <RequiresAuthAdmin>
              <Player />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/tables"
          element={
            <RequiresAuthAdmin>
              <TablesList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/table/:id"
          element={
            <RequiresAuthAdmin>
              <Table />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/products"
          element={
            <RequiresAuthAdmin>
              <ProductsList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/product/:id"
          element={
            <RequiresAuthAdmin>
              <Product />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration"
          element={
            <RequiresAuthAdmin>
              <ConfigurationDashboard />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/orders"
          element={
            <RequiresAuthAdmin>
              <OrdersList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/users"
          element={
            <RequiresAuthAdmin>
              <UsersList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/user/:id"
          element={
            <RequiresAuthAdmin>
              <User />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/streamtypes"
          element={
            <RequiresAuthAdmin>
              <StreamTypesList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/streamtype/:id"
          element={
            <RequiresAuthAdmin>
              <StreamType />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/tenants"
          element={
            <RequiresAuthAdmin>
              <TenantsList />
            </RequiresAuthAdmin>
          }
        />
        <Route
          path="/configuration/tenant/:id"
          element={
            <RequiresAuthAdmin>
              <Tenant />
            </RequiresAuthAdmin>
          }
        />
      </Routes>
    </div>
  );
};

export default App;
